/* eslint-disable react/no-array-index-key */
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import PropTypes from "prop-types";
import { Marker, Popup, Tooltip, useMap } from "react-leaflet";
import L from "leaflet";
import ReactDOMServer from "react-dom/server";
import { Button, Divider, Box, Chip } from "@mui/material";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";

import MDTypography from "../../components/MDTypography";
import Constants, { ButtonTitles, Colors } from "../../utils/Constants";
import SearchableMultiSelectDropdown from "../../components/Dropdown/SearchableMultiSelectDropdown";
import { formatSnagDate, formatSnagStatus } from "../snag/data/SnagData";

// Constants
const ANIMATION_DURATION = 1200;
const ZOOM_THRESHOLD = 0.001;
const TEAM_CHIP_PREVIEW_LIMIT = 6;
const SNAG_INDICATOR_COLOR = "#D92D20";
// Only active snags are shown on the map (closed snags are hidden)
const SNAG_ACTIVE_STATUSES = ["open", "in_discussion"];
// Snag list scrolls inside the popup after ~3 items
const SNAG_LIST_MAX_HEIGHT = 168;
// Keep the popup inside the visible map area
const POPUP_VERTICAL_SPACING = 48;
const POPUP_MIN_HEIGHT = 260;
const POPUP_AUTO_PAN_PADDING = [16, 16];

const SNAG_STATUS_COLORS = {
  open: { bgColor: Colors.LIGHT_RED, color: Colors.DARK_RED },
  in_discussion: { bgColor: Colors.LIGHT_YELLOW, color: Colors.DARK_YELLOW },
  closed: { bgColor: Colors.LIGHT_GREEN, color: Colors.DARK_GREEN },
};

const getChipTextColor = (hexColor) => {
  const normalized = (hexColor || "").replace("#", "");
  if (normalized.length !== 6) return "#fff";

  const red = parseInt(normalized.slice(0, 2), 16);
  const green = parseInt(normalized.slice(2, 4), 16);
  const blue = parseInt(normalized.slice(4, 6), 16);
  const luminance = (0.299 * red + 0.587 * green + 0.114 * blue) / 255;

  return luminance > 0.65 ? "#111827" : "#fff";
};

const getTeamId = (team) =>
  (team?.[Constants.MONGOOSE_ID] || team?.id || team?.value || "").toString();

const getTeamName = (team) => team?.teamsWfmName || team?.title || team?.name || "Team";

const getTeamColor = (team) =>
  team?.color?.startsWith?.("#") ? team.color : `#${team?.color || "D0D5DD"}`;

const normalizeTeamIds = (teamIds = []) => {
  if (!Array.isArray(teamIds)) {
    return [];
  }

  const uniqueTeamIds = [];

  teamIds
    .map((teamId) => (teamId || "").toString())
    .filter(Boolean)
    .forEach((teamId) => {
      if (!uniqueTeamIds.includes(teamId)) {
        uniqueTeamIds.push(teamId);
      }
    });

  return uniqueTeamIds.sort();
};

const areTeamSelectionsEqual = (leftTeamIds = [], rightTeamIds = []) => {
  const normalizedLeftTeamIds = normalizeTeamIds(leftTeamIds);
  const normalizedRightTeamIds = normalizeTeamIds(rightTeamIds);

  if (normalizedLeftTeamIds.length !== normalizedRightTeamIds.length) {
    return false;
  }

  return normalizedLeftTeamIds.every((teamId, index) => teamId === normalizedRightTeamIds[index]);
};

const buildTeamLookup = (teams = []) =>
  teams.reduce((accumulator, team) => {
    const teamId = getTeamId(team);
    if (!teamId) {
      return accumulator;
    }

    return {
      ...accumulator,
      [teamId]: team,
    };
  }, {});

const mapTeamIdsToTeams = (teamIds = [], teamLookup = {}) =>
  (teamIds || []).map((teamId) => teamLookup[(teamId || "").toString()]).filter(Boolean);

const getTeamClusterDisplayMode = (zoomLevel, mapMinZoom, mapMaxZoom) => {
  const zoomRange = Math.max(1, mapMaxZoom - mapMinZoom);
  const normalizedZoom = (zoomLevel - mapMinZoom) / zoomRange;

  return normalizedZoom <= 0.6 ? "count" : "full";
};

const getTeamClusterMetrics = (baseSize, teams = [], displayMode = "full") => {
  const visibleTeamsCount = Math.min(3, teams.length);
  const hasOverflowCount = teams.length > 3;

  if (displayMode === "count") {
    const countBadgeHeight = Math.round(Math.max(18, Math.min(28, baseSize * 0.55)));
    const horizontalPadding = Math.round(Math.max(8, Math.min(12, countBadgeHeight * 0.45)));
    const countFontSize = Math.round(Math.max(10, Math.min(14, countBadgeHeight * 0.56)));
    const countDigitsWidth = Math.max(1, `${teams.length}`.length) * countFontSize * 0.62;
    const width = Math.round(horizontalPadding * 2 + countDigitsWidth);

    return {
      displayMode,
      horizontalPadding,
      countFontSize,
      width,
      height: countBadgeHeight,
    };
  }

  const iconSize = Math.round(Math.max(12, Math.min(20, baseSize * 0.32)));
  const iconGap = Math.round(Math.max(2, Math.min(4, iconSize * 0.18)));
  const containerGap = Math.round(Math.max(4, Math.min(8, iconSize * 0.42)));
  const verticalPadding = Math.round(Math.max(3, Math.min(6, iconSize * 0.28)));
  const horizontalPadding = Math.round(Math.max(6, Math.min(10, iconSize * 0.52)));
  const countHeight = Math.round(Math.max(iconSize, Math.min(24, iconSize * 1.15)));
  const countMinWidth = Math.round(Math.max(22, countHeight + 4));
  const countHorizontalPadding = Math.round(Math.max(5, Math.min(8, iconSize * 0.42)));
  const countWidth = hasOverflowCount ? countMinWidth + countHorizontalPadding * 2 : 0;
  const iconsWidth =
    visibleTeamsCount > 0
      ? visibleTeamsCount * iconSize + Math.max(0, visibleTeamsCount - 1) * iconGap
      : 0;
  const width =
    horizontalPadding * 2 + iconsWidth + (hasOverflowCount ? containerGap + countWidth : 0);
  const height = verticalPadding * 2 + Math.max(iconSize, countHeight);
  const countFontSize = Math.round(Math.max(10, Math.min(12, iconSize * 0.72)));

  return {
    displayMode,
    iconSize,
    iconGap,
    containerGap,
    verticalPadding,
    horizontalPadding,
    countHeight,
    countMinWidth,
    countHorizontalPadding,
    countFontSize,
    width,
    height,
  };
};

const buildTeamClusterIcon = (teams, colorResolver, clusterMetrics) =>
  L.divIcon({
    html: ReactDOMServer.renderToString(
      clusterMetrics.displayMode === "count" ? (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: `${clusterMetrics.width}px`,
            height: `${clusterMetrics.height}px`,
            padding: `0 ${clusterMetrics.horizontalPadding}px`,
            borderRadius: "999px",
            background: "rgba(255,255,255,0.92)",
            boxShadow: "0 2px 8px rgba(15, 23, 42, 0.16)",
            backdropFilter: "blur(2px)",
            boxSizing: "border-box",
            fontSize: `${clusterMetrics.countFontSize}px`,
            fontWeight: 700,
            color: "#344054",
          }}
        >
          {teams.length}
        </div>
      ) : (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: `${clusterMetrics.containerGap}px`,
            padding: `${clusterMetrics.verticalPadding}px ${clusterMetrics.horizontalPadding}px`,
            borderRadius: "999px",
            background: "rgba(255,255,255,0.92)",
            boxShadow: "0 2px 8px rgba(15, 23, 42, 0.16)",
            backdropFilter: "blur(2px)",
            width: `${clusterMetrics.width}px`,
            height: `${clusterMetrics.height}px`,
            boxSizing: "border-box",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: `${clusterMetrics.iconGap}px`,
              flexShrink: 0,
            }}
          >
            {teams.slice(0, 3).map((team, index) => {
              const color = colorResolver(team);
              return (
                <div
                  key={`${team?.[Constants.MONGOOSE_ID] || team?.id || index}-dot`}
                  style={{
                    width: clusterMetrics.iconSize,
                    height: clusterMetrics.iconSize,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <svg
                    width={clusterMetrics.iconSize}
                    height={clusterMetrics.iconSize}
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path
                      fill={color}
                      d="M12 12c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm0 2c-3.33 0-10 1.67-10 5v3h20v-3c0-3.33-6.67-5-10-5z"
                    />
                  </svg>
                </div>
              );
            })}
          </div>
          {teams.length > 3 && (
            <div
              style={{
                fontSize: `${clusterMetrics.countFontSize}px`,
                fontWeight: 700,
                color: "#344054",
                flexShrink: 0,
                minWidth: `${clusterMetrics.countMinWidth}px`,
                height: `${clusterMetrics.countHeight}px`,
                padding: `0 ${clusterMetrics.countHorizontalPadding}px`,
                borderRadius: "999px",
                background: "#F2F4F7",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxSizing: "border-box",
              }}
            >
              +{teams.length - 3}
            </div>
          )}
        </div>
      )
    ),
    className: "team-marker-cluster-icon",
    iconSize: [clusterMetrics.width, clusterMetrics.height],
    iconAnchor: [Math.round(clusterMetrics.width / 2), Math.round(clusterMetrics.height / 2)],
  });

// Transform raw scopes JSON into { label, value, color }
function transformScopesToSegments(scopes) {
  if (!Array.isArray(scopes)) return [];

  return [...scopes]
    .sort((a, b) => (a.scopeOrder ?? 0) - (b.scopeOrder ?? 0))
    .map((scope) => ({
      label: scope.scopeName,
      value: scope.scopeCompletion,
      color: scope.scopeCompletion === 0 ? "#ccc" : scope.colorCode,
    }));
}

// Calculate size parameters based on zoom and scope count
function calculateSizeParams(zoomLevel, totalScopeCount, mapMinZoom, mapMaxZoom) {
  // Base footprint should be roughly consistent across projects.
  // Projects with fewer total scopes should have larger elements for readability.
  // Inverse relationship: more scopes -> smaller base size per scope ring.
  const baseSizeScale = Math.max(0.3, Math.min(1.0, 7 / Math.max(1, totalScopeCount)));

  // Target a diameter that feels similar regardless of scope count.
  const minSize = 15 * baseSizeScale;
  const maxSize = 45 * baseSizeScale;

  const clampedZoom = Math.min(mapMaxZoom, Math.max(mapMinZoom, zoomLevel));
  const zoomRange = Math.max(1, mapMaxZoom - mapMinZoom);
  const normalizedZoom = (clampedZoom - mapMinZoom) / zoomRange;
  const easedZoom = normalizedZoom ** 1.15;
  const sizeRange = Math.max(10, maxSize - minSize);

  const zoomDelta = normalizedZoom - 0.5;
  const zoomMultiplierRaw = zoomDelta >= 0 ? 1 + zoomDelta * 1.5 : 1 + zoomDelta * 0.75;
  const zoomMultiplier = Math.max(0.5, Math.min(2.0, zoomMultiplierRaw));

  return Math.max(13, (minSize + easedZoom * sizeRange) * zoomMultiplier);
}

// Create SVG rings for each segment
function createRings(segments, baseSize, centerXY, animationProgress, totalScopeCount) {
  // Adjust stroke and spacing based on total count to keep overall footprint stable
  let dynamicMinDivisor = 4;
  let radiusMultiplier = 1;

  if (totalScopeCount === 1) {
    dynamicMinDivisor = 2.2;
    radiusMultiplier = 2.4;
  } else if (totalScopeCount === 2) {
    dynamicMinDivisor = 2.5;
    radiusMultiplier = 1.6;
  } else if (totalScopeCount === 3) {
    dynamicMinDivisor = 3.0;
    radiusMultiplier = 1.2;
  }

  const scaleFactor = Math.max(0.5, 7 / Math.max(dynamicMinDivisor, totalScopeCount));

  const baseStrokeWidth = baseSize * 0.06 * scaleFactor;
  const baseRadius = baseSize * 0.16 * radiusMultiplier;
  const ringSpacing = baseSize * 0.08 * scaleFactor;

  const strokeWidth = Math.max(1.0, baseStrokeWidth);
  const radiusStep = Math.max(1.4, ringSpacing);
  const initialRadius = Math.max(4, baseRadius);

  const dimensionScale = baseSize;
  const percentageFontSize = Math.max(7, dimensionScale * 0.1 * scaleFactor);

  return segments.flatMap((seg, idx) => {
    const currentRadius = initialRadius + idx * radiusStep;
    const circumference = 2 * Math.PI * currentRadius;
    const clampedValue = Math.max(0, Math.min(seg.value, 100));
    const animatedValue = clampedValue * animationProgress;
    const fraction = animatedValue / 100;

    const strokeDasharray =
      fraction > 0 && fraction < 1 ? `${fraction * circumference} ${circumference}` : undefined;

    const angle = (fraction * 360) / 2 - 90;
    const rad = (Math.PI / 180) * angle;
    const textX = centerXY + currentRadius * Math.cos(rad);
    const textY = centerXY + currentRadius * Math.sin(rad);

    return [
      <circle
        key={`bg-${idx}`}
        r={currentRadius}
        cx={centerXY}
        cy={centerXY}
        fill="transparent"
        stroke="#ccc"
        strokeWidth={strokeWidth}
        transform={`rotate(-90 ${centerXY} ${centerXY})`}
      />,
      <circle
        key={`fg-${idx}`}
        r={currentRadius}
        cx={centerXY}
        cy={centerXY}
        fill="transparent"
        stroke={seg.color}
        strokeWidth={strokeWidth}
        strokeDasharray={strokeDasharray}
        transform={`rotate(-90 ${centerXY} ${centerXY})`}
      />,
      <text
        key={`text-${idx}`}
        x={textX}
        y={textY}
        textAnchor="middle"
        dominantBaseline="middle"
        fontSize={percentageFontSize}
        fontWeight="800"
        fill={seg.color}
        style={{
          paintOrder: "stroke",
          stroke: "#fff",
          strokeWidth: Math.max(1, percentageFontSize * 0.25),
        }}
      />,
    ];
  });
}

function MultiLayerProgressDonutMarker({
  center,
  locationId,
  scopes,
  locationName,
  handleLocationClick,
  qhseCount,
  assignedTeamIds,
  teamOptions,
  onLocationTeamsSave,
  canManageTeams = false,
  totalScopeCount = 7,
  snags = [],
  onSnagClick = () => {},
  canAddSnag = false,
  onAddSnagClick = () => {},
}) {
  const map = useMap();
  const markerRef = useRef(null);
  const animationFrameRef = useRef(null);

  const initialZoom = map.getZoom();
  const [currentZoom, setCurrentZoom] = useState(initialZoom);
  const [animationProgress, setAnimationProgress] = useState(0);
  const [selectedTeamIds, setSelectedTeamIds] = useState(assignedTeamIds || []);
  const [isSavingTeams, setIsSavingTeams] = useState(false);

  useEffect(() => {
    setSelectedTeamIds(Array.isArray(assignedTeamIds) ? assignedTeamIds : []);
  }, [assignedTeamIds]);

  // Handle zoom changes
  useEffect(() => {
    const handleZoom = () => {
      const latestZoom = map.getZoom();
      setCurrentZoom((prev) => (Math.abs(prev - latestZoom) > ZOOM_THRESHOLD ? latestZoom : prev));
    };

    map.on("zoom", handleZoom);
    map.on("zoomend", handleZoom);

    return () => {
      map.off("zoom", handleZoom);
      map.off("zoomend", handleZoom);
    };
  }, [map]);

  const zoomLevel = Number.isFinite(currentZoom) ? currentZoom : initialZoom;

  const isValidCenter =
    Array.isArray(center) &&
    center.length === 2 &&
    center.every((v) => typeof v === "number" && Number.isFinite(v));

  if (!isValidCenter || !Array.isArray(scopes) || scopes.length === 0) {
    return null;
  }

  const segments = useMemo(() => transformScopesToSegments(scopes), [scopes]);
  const snagList = (Array.isArray(snags) ? snags : []).filter((snag) =>
    SNAG_ACTIVE_STATUSES.includes(snag?.status)
  );
  const hasSnags = snagList.length > 0;
  const popupMaxHeight = Math.max(POPUP_MIN_HEIGHT, map.getSize().y - POPUP_VERTICAL_SPACING);

  // Animation effect
  useEffect(() => {
    let mounted = true;
    let startTimestamp = null;

    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }

    const animate = (timestamp) => {
      if (!mounted) return;

      if (startTimestamp === null) startTimestamp = timestamp;
      const elapsed = timestamp - startTimestamp;
      const progress = Math.min(1, elapsed / ANIMATION_DURATION);

      setAnimationProgress(progress);

      if (progress < 1) {
        animationFrameRef.current = requestAnimationFrame(animate);
      }
    };

    setAnimationProgress(0);
    animationFrameRef.current = requestAnimationFrame(animate);

    return () => {
      mounted = false;
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    };
  }, [segments]);

  const mapMinZoom = Number.isFinite(map.getMinZoom?.()) ? map.getMinZoom() : 3;
  const mapMaxZoom = Number.isFinite(map.getMaxZoom?.()) ? map.getMaxZoom() : 18;

  const baseSize = useMemo(
    () => calculateSizeParams(zoomLevel, totalScopeCount, mapMinZoom, mapMaxZoom),
    [zoomLevel, totalScopeCount, mapMinZoom, mapMaxZoom]
  );

  const { icon } = useMemo(() => {
    let dynamicMinDivisor = 4;
    let radiusMultiplier = 1;

    if (totalScopeCount === 1) {
      dynamicMinDivisor = 2.2;
      radiusMultiplier = 2.4;
    } else if (totalScopeCount === 2) {
      dynamicMinDivisor = 2.5;
      radiusMultiplier = 1.6;
    } else if (totalScopeCount === 3) {
      dynamicMinDivisor = 3.0;
      radiusMultiplier = 1.2;
    }

    const scaleFactor = Math.max(0.5, 7 / Math.max(dynamicMinDivisor, totalScopeCount));

    const baseStrokeWidth = baseSize * 0.06 * scaleFactor;
    const ringSpacing = baseSize * 0.08 * scaleFactor;

    const strokeWidth = Math.max(1.0, baseStrokeWidth);
    const radiusStep = Math.max(1.4, ringSpacing);
    const initialRadius = Math.max(4, baseSize * 0.16 * radiusMultiplier);

    const baseOuterRadius = initialRadius + Math.max(0, segments.length - 1) * radiusStep;
    const circleRadius = baseOuterRadius + strokeWidth / 2;
    const circleSize = Math.max(baseSize, circleRadius * 2);
    const centerXY = circleSize / 2;

    const dimensionScale = circleSize;
    const locationFontSize = Math.max(10, dimensionScale * 0.14);

    const titleLineHeight = locationFontSize * 1.2;
    const titleSpacing = Math.max(4, dimensionScale * 0.05);
    const verticalPadding = Math.max(2, dimensionScale * 0.02);
    const padding = Math.max(4, strokeWidth * 2);
    const markerDiameter = circleSize + padding * 2;
    const totalHeight = markerDiameter + titleLineHeight + titleSpacing + verticalPadding * 2;
    const iconAnchorY = verticalPadding + titleLineHeight + titleSpacing + padding + circleSize / 2;

    const iconWidth = Math.ceil(markerDiameter);
    const iconHeight = Math.ceil(totalHeight);
    const iconAnchor = [Math.round(iconWidth / 2), Math.round(iconAnchorY)];

    const containerStyle = {
      width: markerDiameter,
      padding: `${verticalPadding}px 0`,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      textAlign: "center",
      boxSizing: "border-box",
      cursor: "pointer",
    };

    const titleStyle = {
      display: "block",
      fontWeight: 1000,
      fontSize: locationFontSize,
      color: "#1f2937",
      letterSpacing: "0.025em",
      lineHeight: 1.2,
      marginBottom: titleSpacing,
      whiteSpace: "normal",
      wordBreak: "break-word",
      textShadow: "0 0 4px #fff, 0 0 8px #fff",
    };

    const rings = createRings(segments, baseSize, centerXY, animationProgress, totalScopeCount);

    const ringsSvg = (
      <svg
        width={markerDiameter}
        height={markerDiameter}
        viewBox={`${-padding} ${-padding} ${circleSize + padding * 2} ${circleSize + padding * 2}`}
        preserveAspectRatio="xMidYMid meet"
      >
        {rings}
      </svg>
    );

    const snagBadgeSize = Math.round(Math.max(14, Math.min(26, markerDiameter * 0.32)));

    const svg = (
      <div className="donut-marker-inner" style={containerStyle}>
        <div style={titleStyle}>{locationName}</div>
        {hasSnags ? (
          <div
            style={{
              position: "relative",
              width: markerDiameter,
              height: markerDiameter,
              lineHeight: 0,
            }}
          >
            {ringsSvg}
            <div
              title={Constants.PROJECT_MAP_SNAG_INDICATOR_TITLE}
              style={{
                position: "absolute",
                top: 0,
                right: 0,
                width: snagBadgeSize,
                height: snagBadgeSize,
                borderRadius: "50%",
                backgroundColor: SNAG_INDICATOR_COLOR,
                border: "2px solid #fff",
                boxShadow: "0 1px 4px rgba(0, 0, 0, 0.35)",
                color: "#fff",
                fontWeight: 900,
                fontSize: Math.round(snagBadgeSize * 0.68),
                lineHeight: `${snagBadgeSize - 4}px`,
                textAlign: "center",
                boxSizing: "border-box",
              }}
            >
              !
            </div>
          </div>
        ) : (
          ringsSvg
        )}
      </div>
    );

    return {
      icon: L.divIcon({
        html: ReactDOMServer.renderToString(svg),
        className: "donut-marker-container",
        iconSize: [iconWidth, iconHeight],
        iconAnchor,
      }),
    };
  }, [animationProgress, baseSize, hasSnags, locationName, segments, totalScopeCount]);

  // Update marker icon when it changes
  useEffect(() => {
    if (markerRef.current && icon) {
      markerRef.current.setIcon(icon);
    }
  }, [icon]);

  const handlePopupClick = useCallback(() => {
    handleLocationClick();
  }, [handleLocationClick]);

  const handleAddSnagClick = useCallback(() => {
    markerRef.current?.closePopup?.();
    onAddSnagClick({ locationId, locationName });
  }, [locationId, locationName, onAddSnagClick]);

  const handleTeamSelectionChange = useCallback(
    (name, value) => {
      if (!canManageTeams) {
        return;
      }

      if (name === "teamIds") {
        setSelectedTeamIds(Array.isArray(value) ? value : []);
      }
    },
    [canManageTeams]
  );

  const handleSaveTeams = useCallback(async () => {
    if (!canManageTeams || !locationId || !onLocationTeamsSave) {
      return;
    }

    setIsSavingTeams(true);
    try {
      const result = await onLocationTeamsSave({ locationId, teamIds: selectedTeamIds });
      if (result?.success) {
        markerRef.current?.closePopup?.();
      }
    } finally {
      setIsSavingTeams(false);
    }
  }, [canManageTeams, locationId, onLocationTeamsSave, selectedTeamIds]);

  const teamLookup = useMemo(() => buildTeamLookup(teamOptions), [teamOptions]);

  const selectedTeams = useMemo(
    () => mapTeamIdsToTeams(selectedTeamIds, teamLookup),
    [selectedTeamIds, teamLookup]
  );

  const hasTeamSelectionChanges = useMemo(
    () => !areTeamSelectionsEqual(selectedTeamIds, assignedTeamIds),
    [assignedTeamIds, selectedTeamIds]
  );

  const assignedTeams = useMemo(
    () => mapTeamIdsToTeams(assignedTeamIds, teamLookup),
    [assignedTeamIds, teamLookup]
  );

  const selectedTeamsPreview = useMemo(
    () => selectedTeams.slice(0, TEAM_CHIP_PREVIEW_LIMIT),
    [selectedTeams]
  );

  const remainingSelectedTeamsCount = Math.max(0, selectedTeams.length - TEAM_CHIP_PREVIEW_LIMIT);

  const teamClusterDisplayMode = useMemo(
    () => getTeamClusterDisplayMode(zoomLevel, mapMinZoom, mapMaxZoom),
    [mapMaxZoom, mapMinZoom, zoomLevel]
  );

  const teamClusterMetrics = useMemo(
    () => getTeamClusterMetrics(baseSize, assignedTeams, teamClusterDisplayMode),
    [assignedTeams, baseSize, teamClusterDisplayMode]
  );

  const teamOverlayMarkers = useMemo(() => {
    if (!Array.isArray(assignedTeams) || assignedTeams.length === 0) {
      return [];
    }

    const originPoint = map.latLngToLayerPoint(center);
    const mapSize = map.getSize();
    const horizontalOffset = Math.max(teamClusterMetrics.width * 0.28, baseSize * 0.42);
    const verticalOffset = Math.max(teamClusterMetrics.height * 1.15, baseSize * 1.05);
    const clusterPosition = map.layerPointToLatLng(
      L.point(originPoint.x - horizontalOffset, originPoint.y + verticalOffset)
    );
    const clusterContainerPoint = map.latLngToContainerPoint(clusterPosition);
    const horizontalPadding = 220;
    const verticalPadding = 140;

    let tooltipDirection = "top";
    let tooltipOffset = [0, -10];

    if (clusterContainerPoint.y < verticalPadding) {
      tooltipDirection = "bottom";
      tooltipOffset = [0, 10];
    } else if (clusterContainerPoint.x < horizontalPadding) {
      tooltipDirection = "right";
      tooltipOffset = [10, 0];
    } else if (clusterContainerPoint.x > mapSize.x - horizontalPadding) {
      tooltipDirection = "left";
      tooltipOffset = [-10, 0];
    } else if (clusterContainerPoint.y > mapSize.y - verticalPadding) {
      tooltipDirection = "top";
      tooltipOffset = [0, -10];
    }

    return [
      {
        key: `${locationId}-team-cluster`,
        position: [clusterPosition.lat, clusterPosition.lng],
        icon: buildTeamClusterIcon(assignedTeams, getTeamColor, teamClusterMetrics),
        teams: assignedTeams,
        tooltipDirection,
        tooltipOffset,
      },
    ];
  }, [assignedTeams, baseSize, center, locationId, map, teamClusterMetrics]);

  return (
    <>
      <style>
        {`
          .donut-marker-container:hover {
            z-index: 1000 !important;
          }
          .donut-marker-inner {
            transition: transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
          }
          .donut-marker-container:hover .donut-marker-inner {
            transform: scale(1.4);
            filter: drop-shadow(0 10px 15px rgba(0, 0, 0, 0.2));
          }
        `}
      </style>
      {teamOverlayMarkers.map((teamMarker) => (
        <Marker
          key={teamMarker.key}
          position={teamMarker.position}
          icon={teamMarker.icon}
          bubblingMouseEvents={false}
          keyboard={false}
        >
          <Tooltip
            direction={teamMarker.tooltipDirection}
            offset={teamMarker.tooltipOffset}
            opacity={1}
            sticky
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 0.5,
                minWidth: 120,
                maxHeight: 220,
                overflowY: "auto",
                pr: 0.5,
              }}
            >
              {teamMarker.teams.map((team, index) => {
                const color = getTeamColor(team);

                return (
                  <Box
                    key={`${teamMarker.key}-${getTeamId(team) || index}`}
                    sx={{ display: "flex", alignItems: "center", gap: 0.75 }}
                  >
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: "50%",
                        backgroundColor: color,
                        flexShrink: 0,
                      }}
                    />
                    <MDTypography
                      variant="caption"
                      sx={{ fontWeight: 600, color: "#111827", whiteSpace: "nowrap" }}
                    >
                      {getTeamName(team)}
                    </MDTypography>
                  </Box>
                );
              })}
            </Box>
          </Tooltip>
        </Marker>
      ))}
      <Marker ref={markerRef} position={center} icon={icon}>
        <Popup maxHeight={popupMaxHeight} autoPanPadding={POPUP_AUTO_PAN_PADDING}>
          <Box sx={{ minWidth: 240, p: 1 }}>
            <MDTypography variant="h6" sx={{ mb: 1.5, fontWeight: "bold" }}>
              {locationName}
            </MDTypography>
            <ul style={{ padding: 0, margin: 0 }}>
              {segments.map((seg, idx) => (
                <li
                  key={idx}
                  style={{
                    listStyle: "none",
                    marginBottom: "10px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    columnGap: 12,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", columnGap: 1.5, flex: 1 }}>
                    <Box
                      sx={{
                        width: 14,
                        height: 14,
                        backgroundColor: seg.color,
                        borderRadius: "3px",
                        flexShrink: 0,
                      }}
                    />
                    <MDTypography variant="button" sx={{ color: "#374151", fontWeight: 500 }}>
                      {seg.label}
                    </MDTypography>
                  </Box>
                  <MDTypography variant="button" sx={{ fontWeight: "bold", color: "#111827" }}>
                    {(Math.max(0, Math.min(seg.value, 100)) * animationProgress).toFixed(1)}%
                  </MDTypography>
                </li>
              ))}
              <Divider sx={{ my: 1.5 }} />
              <li
                style={{
                  listStyle: "none",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  columnGap: 12,
                }}
              >
                <MDTypography variant="button" sx={{ color: "#374151", fontWeight: 500 }}>
                  {Constants.PROJECT_MAP_TOTAL_QHSE_CARDS_LABEL}
                </MDTypography>
                <MDTypography variant="button" sx={{ fontWeight: "bold", color: "#111827" }}>
                  {qhseCount}
                </MDTypography>
              </li>
            </ul>
            <Divider sx={{ my: 1.5 }} />
            {canManageTeams && (
              <SearchableMultiSelectDropdown
                id={`location-teams-${locationId}`}
                name="teamIds"
                label={Constants.PROJECT_MAP_TEAMS_LABEL}
                hint={Constants.PROJECT_MAP_TEAMS_HINT}
                menu={teamOptions}
                values={selectedTeamIds}
                handleChange={handleTeamSelectionChange}
                showBadge
                dropdownIcon={GroupsOutlinedIcon}
                marginBottom={0}
                valueStyle={{
                  backgroundColor: "#fff",
                  menuWidth: 260,
                  inputWidth: 180,
                  verticalMarginTop: 8,
                }}
              />
            )}
            <MDTypography variant="button" sx={{ color: "#374151", fontWeight: 500, mt: 1.25 }}>
              {Constants.PROJECT_MAP_TEAMS_LABEL}
            </MDTypography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 1.25 }}>
              {selectedTeams.length > 0 ? (
                <>
                  {selectedTeamsPreview.map((team) => {
                    const color = getTeamColor(team);
                    return (
                      <Chip
                        key={getTeamId(team)}
                        label={getTeamName(team)}
                        size="small"
                        sx={{
                          backgroundColor: color,
                          color: getChipTextColor(color),
                          fontWeight: 600,
                          maxWidth: 140,
                          "& .MuiChip-label": {
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          },
                        }}
                      />
                    );
                  })}
                  {remainingSelectedTeamsCount > 0 && (
                    <Chip
                      label={`+${remainingSelectedTeamsCount} more`}
                      size="small"
                      sx={{
                        backgroundColor: "#EEF2F6",
                        color: "#344054",
                        fontWeight: 700,
                      }}
                    />
                  )}
                </>
              ) : (
                <MDTypography variant="caption" sx={{ color: "#667085" }}>
                  {Constants.PROJECT_MAP_NO_TEAMS_ASSIGNED}
                </MDTypography>
              )}
            </Box>
            {selectedTeams.length > TEAM_CHIP_PREVIEW_LIMIT && (
              <MDTypography variant="caption" sx={{ color: "#667085", mt: 0.75, display: "block" }}>
                Showing first {TEAM_CHIP_PREVIEW_LIMIT} selected teams in preview.
              </MDTypography>
            )}
            {canManageTeams && (hasTeamSelectionChanges || isSavingTeams) && (
              <Button
                variant="outlined"
                fullWidth
                sx={{
                  mt: 1.5,
                  textTransform: "none",
                }}
                onClick={handleSaveTeams}
                disabled={isSavingTeams || !hasTeamSelectionChanges}
              >
                {isSavingTeams ? ButtonTitles.LOADING : ButtonTitles.SAVE}
              </Button>
            )}
            {hasSnags && (
              <Box sx={{ mt: 1.5 }}>
                <Divider sx={{ mb: 1.25 }} />
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                  <Box
                    sx={{
                      width: 16,
                      height: 16,
                      borderRadius: "50%",
                      backgroundColor: SNAG_INDICATOR_COLOR,
                      color: "#fff",
                      fontSize: 11,
                      fontWeight: 900,
                      lineHeight: "16px",
                      textAlign: "center",
                      flexShrink: 0,
                    }}
                  >
                    !
                  </Box>
                  <MDTypography variant="button" sx={{ color: "#374151", fontWeight: 500 }}>
                    {`${Constants.PROJECT_MAP_SNAGS_LABEL} (${snagList.length})`}
                  </MDTypography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.75,
                    mt: 1,
                    maxHeight: SNAG_LIST_MAX_HEIGHT,
                    overflowY: "auto",
                    pr: 0.5,
                  }}
                >
                  {snagList.map((snag, index) => {
                    const statusColors =
                      SNAG_STATUS_COLORS[snag?.status] || SNAG_STATUS_COLORS.open;
                    return (
                      <Box
                        key={snag?.[Constants.MONGOOSE_ID] || index}
                        role="button"
                        tabIndex={0}
                        title={snag?.details}
                        onClick={() => onSnagClick(snag)}
                        onKeyDown={(event) => {
                          if (event.key === "Enter") onSnagClick(snag);
                        }}
                        sx={{
                          flexShrink: 0,
                          border: "1px solid #FECDCA",
                          borderLeft: `3px solid ${SNAG_INDICATOR_COLOR}`,
                          borderRadius: "6px",
                          px: 1,
                          py: 0.5,
                          cursor: "pointer",
                          "&:hover": { backgroundColor: "#FEF3F2" },
                        }}
                      >
                        <MDTypography
                          variant="caption"
                          sx={{
                            color: "#111827",
                            fontWeight: 600,
                            display: "block",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            textTransform: "none",
                          }}
                        >
                          {snag?.details}
                        </MDTypography>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            mt: 0.25,
                          }}
                        >
                          <Chip
                            label={formatSnagStatus(snag?.status)}
                            size="small"
                            sx={{
                              height: 18,
                              backgroundColor: statusColors.bgColor,
                              color: statusColors.color,
                              fontWeight: 600,
                              fontSize: 10,
                              textTransform: "capitalize",
                            }}
                          />
                          <MDTypography variant="caption" sx={{ color: "#667085" }}>
                            {formatSnagDate(snag?.dateAdded)}
                          </MDTypography>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            )}
            {canAddSnag && (
              <Button
                variant="outlined"
                fullWidth
                sx={{
                  mt: 1.5,
                  borderColor: `${SNAG_INDICATOR_COLOR} !important`,
                  color: `${SNAG_INDICATOR_COLOR} !important`,
                  "&:hover": { backgroundColor: "#FEF3F2" },
                  textTransform: "none",
                }}
                onClick={handleAddSnagClick}
              >
                {ButtonTitles.ADD_SNAG}
              </Button>
            )}
            <Button
              variant="contained"
              fullWidth
              sx={{
                mt: canAddSnag || hasTeamSelectionChanges || isSavingTeams ? 1 : 1.5,
                backgroundColor: "#1a1a2e",
                color: "#ffffff !important",
                "&:hover": { backgroundColor: "#0f0f1e" },
                textTransform: "none",
              }}
              onClick={handlePopupClick}
            >
              {ButtonTitles.VIEW_DETAILS}
            </Button>
          </Box>
        </Popup>
      </Marker>
    </>
  );
}

MultiLayerProgressDonutMarker.propTypes = {
  center: PropTypes.arrayOf(PropTypes.number).isRequired,
  locationId: PropTypes.string.isRequired,
  scopes: PropTypes.arrayOf(
    PropTypes.shape({
      scopeName: PropTypes.string.isRequired,
      scopeCompletion: PropTypes.number.isRequired,
    })
  ).isRequired,
  locationName: PropTypes.string.isRequired,
  handleLocationClick: PropTypes.func.isRequired,
  qhseCount: PropTypes.number.isRequired,
  assignedTeamIds: PropTypes.arrayOf(PropTypes.string),
  teamOptions: PropTypes.arrayOf(PropTypes.object),
  onLocationTeamsSave: PropTypes.func,
  canManageTeams: PropTypes.bool,
  totalScopeCount: PropTypes.number,
  snags: PropTypes.arrayOf(PropTypes.object),
  onSnagClick: PropTypes.func,
  canAddSnag: PropTypes.bool,
  onAddSnagClick: PropTypes.func,
};

MultiLayerProgressDonutMarker.defaultProps = {
  assignedTeamIds: [],
  teamOptions: [],
  onLocationTeamsSave: () => ({ success: false }),
  canManageTeams: false,
  totalScopeCount: 7,
  snags: [],
  onSnagClick: () => {},
  canAddSnag: false,
  onAddSnagClick: () => {},
};

export default MultiLayerProgressDonutMarker;
